import React, { useState, useMemo } from 'react';
import { TeamInLeague, Player, TrainingFocus, Position } from '../../../domain/entities';
import { PlayerFace } from '../../shared/PlayerFace';
import { View } from '../../../App';
import { TRAINING_FOCUS_OPTIONS, INDIVIDUAL_PLAYER_ATTRIBUTES } from '../../../domain/constants';
import { useTableFilters } from '../../../hooks/useTableFilters';
import { TableHeader } from '../../shared/TableHeader';
import { AdvancedFilterModal } from '../../shared/AdvancedFilterModal';

interface TrainingIndividualPanelProps {
    team: TeamInLeague;
    onUpdatePlayerTrainingFocus: (playerId: number, focus: TrainingFocus | null) => void;
    pushView: (view: View) => void;
    facilitiesLevel: number;
}

const positionOrder: Record<Position, number> = { GK: 1, DEF: 2, MID: 3, FWD: 4 };

export const TrainingIndividualPanel: React.FC<TrainingIndividualPanelProps> = ({ team, onUpdatePlayerTrainingFocus, pushView, facilitiesLevel }) => {
    // Filter state
    const {
        filters: tableFilters,
        updateFilter,
        clearFilters,
        isFilterModalOpen,
        setIsFilterModalOpen,
        hasActiveFilters
    } = useTableFilters('training-individual', {
        search: '',
        sortBy: 'position_asc',
        positionFilter: 'ALL',
        minSkill: 0,
        maxSkill: 99,
        minAge: 16,
        maxAge: 40,
        favFootFilter: 'ALL',
    });

    const getEffectiveFocus = (player: Player): TrainingFocus => {
        return player.trainingFocus || team.positionalTrainingFocus[player.position] || team.trainingFocus;
    };

    const filteredAndSortedSquad = useMemo(() => {
        return [...team.squad]
            .filter(p => p.name.toLowerCase().includes(tableFilters.search.toLowerCase()))
            .filter(p => tableFilters.positionFilter === 'ALL' || p.position === tableFilters.positionFilter)
            .filter(p => p.skill >= tableFilters.minSkill && p.skill <= tableFilters.maxSkill)
            .filter(p => p.age >= tableFilters.minAge && p.age <= tableFilters.maxAge)
            .filter(p => tableFilters.favFootFilter === 'ALL' || p.favoredFoot === tableFilters.favFootFilter)
            .sort((a, b) => {
                switch (tableFilters.sortBy) {
                    case 'skill_asc': return a.skill - b.skill;
                    case 'potential_desc': return b.potential - a.potential;
                    case 'potential_asc': return a.potential - b.potential;
                    case 'fitness_desc': return b.fitness - a.fitness;
                    case 'fitness_asc': return a.fitness - b.fitness;
                    case 'position_asc': return positionOrder[a.position] - positionOrder[b.position] || b.skill - a.skill;
                    case 'name_asc': return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                    case 'name_desc': return b.name < a.name ? -1 : b.name > a.name ? 1 : 0;
                    case 'age_asc': return a.age - b.age;
                    case 'age_desc': return b.age - a.age;
                    case 'skill_desc':
                    default:
                        return b.skill - a.skill;
                }
            });
    }, [team.squad, tableFilters]);

    return (
        <div className="panel">
            <div className="panel-header">Individual Focus</div>
            <div className="panel-content" style={{ padding: 0 }}>
                {facilitiesLevel < 3 ? (
                    <div style={{ padding: '24px 12px', textAlign: 'center', opacity: 0.7 }}>
                        <p><strong>Individual player training is available from Facility Level 3 onwards.</strong></p>
                        <p>Upgrade your training facilities to unlock individual player attribute targeting.</p>
                    </div>
                ) : (
                    <>
                 <p style={{ padding: '12px 12px 0 12px' }}>Target specific attributes for individual players. This will override both team and positional settings.</p>
                <TableHeader
                    searchValue={tableFilters.search}
                    onSearchChange={(value) => updateFilter('search', value)}
                    searchPlaceholder="Search player names..."
                    sortValue={tableFilters.sortBy}
                    onSortChange={(value) => updateFilter('sortBy', value)}
                    sortOptions={[
                        { label: 'Position (default)', value: 'position_asc' },
                        { label: 'Skill (High to Low)', value: 'skill_desc' },
                        { label: 'Skill (Low to High)', value: 'skill_asc' },
                        { label: 'Potential (High to Low)', value: 'potential_desc' },
                        { label: 'Potential (Low to High)', value: 'potential_asc' },
                        { label: 'Fitness (High to Low)', value: 'fitness_desc' },
                        { label: 'Fitness (Low to High)', value: 'fitness_asc' },
                        { label: 'Age (Young to Old)', value: 'age_asc' },
                        { label: 'Age (Old to Young)', value: 'age_desc' },
                        { label: 'Name (A to Z)', value: 'name_asc' },
                        { label: 'Name (Z to A)', value: 'name_desc' },
                    ]}
                    onFilterClick={() => setIsFilterModalOpen(true)}
                    onClearFilters={clearFilters}
                    hasActiveFilters={hasActiveFilters}
                />
                <div className="table-wrapper">
                    <table className="table individual-training-table">
                        <thead>
                            <tr>
                                <th>Player</th>
                                <th>Age</th>
                                <th>Pos</th>
                                <th>Effective Focus</th>
                                <th>Individual Setting</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAndSortedSquad.map(player => (
                                <tr key={player.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <PlayerFace player={player} teamColor={team.colors.primary} />
                                            <button className="link-style" onClick={() => pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id })}>
                                                {player.name}
                                            </button>
                                        </div>
                                    </td>
                                    <td>{player.age}</td>
                                    <td>{player.position}</td>
                                    <td>{getEffectiveFocus(player)}</td>
                                    <td>
                                        <select value={player.trainingFocus || 'default'} onChange={e => onUpdatePlayerTrainingFocus(player.id, e.target.value === 'default' ? null : e.target.value as TrainingFocus)}>
                                            <option value="default">Default</option>
                                            <optgroup label="Position-Based">
                                                {TRAINING_FOCUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                            </optgroup>
                                            <optgroup label="Attributes">
                                                {INDIVIDUAL_PLAYER_ATTRIBUTES.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                            </optgroup>
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <AdvancedFilterModal
                    isOpen={isFilterModalOpen}
                    onClose={() => setIsFilterModalOpen(false)}
                    filters={tableFilters}
                    onFilterChange={updateFilter}
                    onClearFilters={clearFilters}
                    filterOptions={[
                        { label: 'Position', type: 'select', key: 'positionFilter', options: [
                            { label: 'All Positions', value: 'ALL' },
                            { label: 'Goalkeeper', value: 'GK' },
                            { label: 'Defender', value: 'DEF' },
                            { label: 'Midfielder', value: 'MID' },
                            { label: 'Forward', value: 'FWD' },
                        ]},
                        { label: 'Preferred Foot', type: 'select', key: 'favFootFilter', options: [
                            { label: 'All Feet', value: 'ALL' },
                            { label: 'Left', value: 'Left' },
                            { label: 'Right', value: 'Right' },
                        ]},
                        { label: 'Min Skill', type: 'range', key: 'minSkill', min: 0, max: 99, step: 1 },
                        { label: 'Max Skill', type: 'range', key: 'maxSkill', min: 0, max: 99, step: 1 },
                        { label: 'Min Age', type: 'range', key: 'minAge', min: 16, max: 40, step: 1 },
                        { label: 'Max Age', type: 'range', key: 'maxAge', min: 16, max: 40, step: 1 },
                    ]}
                    title="Training Filters"
                />
                    </>
                )}
            </div>
        </div>
    );
};